import { TOOLS, toolById } from '~/lib/tools';
import type { Tool } from '~/types/game';

/**
 * Pure shop helpers: a deterministic per-node stock subset of the tool catalog,
 * and a tool price. No store state — both are functions of stable inputs (node
 * id, tool), so the same shop always stocks the same items at the same base
 * price (persistence-friendly). Rarity tiers / spawn weighting are future depth.
 */

// ── Deterministic string-seeded PRNG (xmur3 → mulberry32) ──────────────────
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function rngFor(id: string): () => number {
  return mulberry32(xmur3(id)());
}

/** A shop stocks a deterministic subset of this many tools. */
export const SHOP_MIN_STOCK = 2;
export const SHOP_MAX_STOCK = 4;

/** The deterministic stock for a shop node: a seeded subset (2–4) of the tool
 *  catalog, ordered by id for a stable display. Same node id → same stock. */
export function shopStock(nodeId: string): Tool[] {
  const rng = rngFor(nodeId);
  const ids = TOOLS.map((t) => t.id);
  // Fisher–Yates shuffle with the seeded rng.
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = ids[i]!;
    ids[i] = ids[j]!;
    ids[j] = a;
  }
  const n = SHOP_MIN_STOCK + Math.floor(rng() * (SHOP_MAX_STOCK - SHOP_MIN_STOCK + 1));
  return ids
    .slice(0, Math.min(n, ids.length))
    .map((id) => toolById(id))
    .filter((t): t is Tool => t !== undefined)
    .sort((x, y) => x.id.localeCompare(y.id));
}

// ── Pricing (¤) ────────────────────────────────────────────────────────────
/** Base price plus a per-strength premium, so stronger tools cost more.
 *  probe(15) → 190 … sieve(28) → 320; with a 500 ¤ start, a fresh operator can
 *  afford a purchase or two. Tunable. */
export const TOOL_PRICE_BASE = 40;
export const TOOL_PRICE_PER_STRENGTH = 10;

export function toolPrice(tool: Tool): number {
  return Math.round(TOOL_PRICE_BASE + tool.strength * TOOL_PRICE_PER_STRENGTH);
}
